import React, { PureComponent } from 'react';

import styled from 'styled-components';

import Box from '../atoms/Box/Box';
import Button from '../atoms/Button/Button';
import Image from '../atoms/Image/Image';
import Text from '../atoms/Text/Text';

import { Color } from '@shared/enums/button-color';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { Size } from '@shared/enums/button-size';
import { Transform } from '@shared/enums/transform';
import { breakpoint } from 'theme';
import { withTranslation } from 'i18n';

const BoxWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: center;

  ${breakpoint.m`
    flex-direction: row;
    align-items: center;
  `}
`;

const BoxButtons = styled(Box)`
  display: flex;
  flex-direction: column;

  ${breakpoint.m`
    ${Button} {
      width: 14.875em;
    }
  `}

  ${breakpoint.lg`
    flex-direction: row;
  `}
`;

const BoxBenefits = styled(Box)`
  display: block;
  margin-top: 3rem;

  ${breakpoint.lg`
    margin-top: 0;
  `}
`;

const UlBenefits = styled.ul`
  margin-left: 1.25em;

  li {
    position: relative;
    font-size: 1.125em;
    font-weight: normal;
    margin-bottom: 0.625em;
    color: ${props => props.theme.colors.cornflowerBlue};
    line-height: normal;

    &::before {
      content: '\\2022';
      font-size: 2.25em;
      color: ${props => props.theme.colors.abyss};
      position: absolute;
      top: -16px;
      left: -20px;
    }
  }
`;

type NotAuthorizedTemplateProps = {
  onLogin: () => void;
  onSignUp: () => void;
  isB2C: boolean;
  t: I18nTranslation;

  /**
   * Are translations ready
   */
  tReady: boolean;
};

class NotAuthorizedTemplate extends PureComponent<NotAuthorizedTemplateProps> {
  t = (key, options?) => this.props.t(`noAuth.${key}`, options);

  renderBenefitsList = () => {
    const items = this.t('benefits.items', { returnObjects: true });

    return (Array.isArray(items) ? items : []).map((benefit, i) => (
      <li key={i}>{benefit}</li>
    ));
  };

  getBenefits = () => {
    return (
      !this.props.isB2C && (
        <BoxBenefits>
          <Box mb={0.625}>
            <Text
              size={1.375}
              color="cornflowerBlue"
              weight="bold"
              transform={Transform.Uppercase}
            >
              {this.t('benefits.title')}
            </Text>
          </Box>
          <UlBenefits>
            {this.props.tReady && this.renderBenefitsList()}
          </UlBenefits>
        </BoxBenefits>
      )
    );
  };

  getRegisterButton = () =>
    !this.props.isB2C && (
      <Button
        sizeType={Size.FullWidth}
        colorType={Color.FullTransparentBorderAbyss}
        onClick={this.props.onSignUp}
      >
        <Text
          smSize={1.125}
          size={1.375}
          weight={600}
          transform={Transform.Uppercase}
        >
          {this.t('register')}
        </Text>
      </Button>
    );

  render() {
    const { onLogin } = this.props;

    return (
      <BoxWrapper
        pt={2.625}
        pb={2.625}
        pl={1.5}
        pr={1.5}
        mPl={3.625}
        mPr={1.625}
        lgPl={9.75}
        lgPr={3.375}
      >
        <Box smMb={2.625} mMr={2.5} lgMr={7}>
          <Box smMb={1} mb={1.625}>
            <Text
              size={2.625}
              lgSize={3.75}
              color="cornflowerBlue"
              weight="bold"
            >
              {this.t('title')}
            </Text>
          </Box>
          <Box mb={1.5} mMb={2.875} lgMb={4.375}>
            <Text
              size={1.125}
              lgSize={1.375}
              color="cornflowerBlue"
              weight="normal"
              lineHeight={1.33}
            >
              {this.t('description')}
            </Text>
          </Box>
          <BoxButtons lgMb={4}>
            <Box mb={0.625} lgMb={0} lgMr={1.125}>
              <Button
                sizeType={Size.FullWidth}
                colorType={Color.Abyss}
                onClick={onLogin}
              >
                <Text
                  smSize={1.125}
                  size={1.375}
                  weight={600}
                  transform={Transform.Uppercase}
                >
                  {this.t('login')}
                </Text>
              </Button>
            </Box>
            {this.getRegisterButton()}
          </BoxButtons>
          {this.getBenefits()}
        </Box>
        <Image
          src="https://cdn.3d4medical.com/store/images/notAuthorized.png"
          width={17}
          mWidth={21.375}
          lgWidth={35.5}
        />
      </BoxWrapper>
    );
  }
}

export default withTranslation('account')(NotAuthorizedTemplate);
